import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { getAuthConfig, isAuthenticated, logout } from '../utils/auth';
import AppNav from '../components/AppNav';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');
const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

function UpdateCardForm({ subscriptionId, planName, domain, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      const { error: submitErr } = await elements.submit();
      if (submitErr) { setError(submitErr.message); setProcessing(false); return; }

      // Confirm the SetupIntent to save the payment method
      const { setupIntent, error: confirmErr } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: `${window.location.origin}/subscriptions` },
        redirect: 'if_required',
      });
      if (confirmErr) { setError(confirmErr.message); setProcessing(false); return; }

      // Attach + set as default, and retry any outstanding invoice
      const res = await axios.post(`${API_URL}/api/subscriptions/update-payment-method`, {
        subscriptionId,
        paymentMethodId: setupIntent.payment_method,
      }, getAuthConfig());

      // If there is a 3DS-required invoice, handle it
      if (res.data.invoiceClientSecret) {
        const { error: actionErr } = await stripe.handleNextAction({ clientSecret: res.data.invoiceClientSecret });
        if (actionErr) { setError(actionErr.message); setProcessing(false); return; }
      }

      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to update payment method';
      setError(msg);
      onError(msg);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <div style={{ marginTop: '14px', padding: '12px 16px', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.35)', borderRadius: '8px', color: '#f87171', fontSize: '13px' }}>
          <i className="fa-solid fa-circle-xmark" style={{ marginRight: 6 }} />{error}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{ marginTop: '20px', width: '100%', padding: '14px', background: processing ? '#555' : 'linear-gradient(135deg,#40E0D0,#2dd4bf)', border: 'none', borderRadius: '8px', color: '#111', fontWeight: '700', fontSize: '15px', cursor: processing ? 'not-allowed' : 'pointer' }}
      >
        {processing ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }} />Saving…</> : 'Save & Update Payment Method'}
      </button>
    </form>
  );
}

export default function UpdatePaymentMethodPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const sub = location.state?.subscription;

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    if (!sub?.id) { navigate('/subscriptions'); return; }
    axios.post(`${API_URL}/api/subscriptions/create-update-setup-intent`, { subscriptionId: sub.id }, getAuthConfig())
      .then(r => setClientSecret(r.data.clientSecret))
      .catch(e => setError(e.response?.data?.error || 'Failed to initialize. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  if (success) {
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={handleLogout} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px', textAlign: 'center', padding: '24px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fa-solid fa-circle-check" style={{ fontSize: '32px', color: '#10b981' }} />
          </div>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '22px' }}>Payment Method Updated</h2>
          <p style={{ color: '#9ca3af', margin: 0, maxWidth: '360px', lineHeight: '1.6' }}>
            Your payment method has been saved{sub?.status === 'past_due' ? ' and your outstanding invoice has been paid.' : '.'}
          </p>
          <button onClick={() => navigate('/subscriptions')} style={{ marginTop: '8px', padding: '12px 28px', background: 'linear-gradient(135deg,#40E0D0,#2dd4bf)', border: 'none', borderRadius: '8px', color: '#111', fontWeight: '700', cursor: 'pointer', fontSize: '14px' }}>
            <i className="fa-solid fa-list" style={{ marginRight: 6 }} />Back to Subscriptions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="checkout-layout">
        <div className="checkout-main">
          <h1 className="page-title">Update Payment Method</h1>

          {sub && (
            <div style={{ background: 'rgba(64,224,208,0.07)', border: '1px solid rgba(64,224,208,0.2)', borderRadius: '10px', padding: '14px 18px', marginBottom: '24px' }}>
              <p style={{ margin: 0, color: '#9ca3af', fontSize: '13px' }}>Updating card for</p>
              <p style={{ margin: '4px 0 0', color: '#fff', fontWeight: '600', fontSize: '15px' }}>
                {sub.planName || sub.service}{sub.domain ? ` · ${sub.domain}` : ''}
              </p>
              {sub.status === 'past_due' && (
                <p style={{ margin: '6px 0 0', color: '#f59e0b', fontSize: '12px' }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
                  Your subscription has a failed payment. Adding a new card will automatically retry it.
                </p>
              )}
            </div>
          )}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <div className="loading-spinner" />
            </div>
          )}

          {error && (
            <div style={{ padding: '14px 18px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '8px', color: '#f87171', fontSize: '13px' }}>
              <i className="fa-solid fa-circle-xmark" style={{ marginRight: 6 }} />{error}
            </div>
          )}

          {!loading && clientSecret && (
            <div className="checkout-form-new">
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#40E0D0', colorBackground: '#2d2d2d', colorText: '#ffffff', fontFamily: 'system-ui,sans-serif', borderRadius: '8px' } } }}>
                <UpdateCardForm
                  subscriptionId={sub?.id}
                  planName={sub?.planName}
                  domain={sub?.domain}
                  onSuccess={() => setSuccess(true)}
                  onError={msg => setError(msg)}
                />
              </Elements>
            </div>
          )}
        </div>

        <div className="checkout-sidebar">
          <div className="order-summary">
            <h3>Security</h3>
            <p style={{ color: '#9ca3af', fontSize: '13px', lineHeight: '1.6' }}>
              Your card details are encrypted and handled directly by Stripe. We never store your full card number.
            </p>
            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '12px' }}>
              <i className="fa-solid fa-lock" style={{ color: '#40E0D0' }} />
              256-bit SSL encryption
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
